import { BULK_TRANSFER_CHUNK_MAX_BYTES, type BulkTransferChunk } from "@cmmd-forge/contracts";

export interface ChunkedBulkTransfer {
  readonly totalBytes: number;
  readonly sha256: string;
  readonly chunks: ReadonlyArray<BulkTransferChunk>;
}

const BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/;

export function encodeBase64Url(bytes: Uint8Array): string {
  let output = "";
  for (let index = 0; index < bytes.byteLength; index += 3) {
    const first = bytes[index] ?? 0;
    const second = bytes[index + 1] ?? 0;
    const third = bytes[index + 2] ?? 0;
    const value = (first << 16) | (second << 8) | third;
    output += BASE64_ALPHABET[(value >>> 18) & 63];
    output += BASE64_ALPHABET[(value >>> 12) & 63];
    if (index + 1 < bytes.byteLength) output += BASE64_ALPHABET[(value >>> 6) & 63];
    if (index + 2 < bytes.byteLength) output += BASE64_ALPHABET[value & 63];
  }
  return output.replaceAll("+", "-").replaceAll("/", "_");
}

export function decodeBase64Url(value: string): Uint8Array {
  if (value.length === 0 || !BASE64URL_PATTERN.test(value)) {
    throw new Error("Bulk transfer chunk is not valid base64url data.");
  }

  const normalized = value.replaceAll("-", "+").replaceAll("_", "/");
  const outputLength = Math.floor((normalized.length * 6) / 8);
  const output = new Uint8Array(outputLength);
  let accumulator = 0;
  let bits = 0;
  let outputIndex = 0;

  for (const character of normalized) {
    const index = BASE64_ALPHABET.indexOf(character);
    if (index < 0) throw new Error("Bulk transfer chunk is not valid base64url data.");
    accumulator = (accumulator << 6) | index;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      output[outputIndex] = (accumulator >>> bits) & 0xff;
      outputIndex += 1;
    }
  }

  return output;
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const input = new Uint8Array(bytes.byteLength);
  input.set(bytes);
  const digest = await crypto.subtle.digest("SHA-256", input.buffer);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function chunkBulkTransfer(
  bytes: Uint8Array,
  options?: { readonly chunkBytes?: number; readonly transferId?: string },
): Promise<ChunkedBulkTransfer> {
  const chunkBytes = options?.chunkBytes ?? BULK_TRANSFER_CHUNK_MAX_BYTES;
  if (
    !Number.isSafeInteger(chunkBytes) ||
    chunkBytes < 1 ||
    chunkBytes > BULK_TRANSFER_CHUNK_MAX_BYTES
  ) {
    throw new Error(
      `Bulk transfer chunk size must be between 1 and ${BULK_TRANSFER_CHUNK_MAX_BYTES}.`,
    );
  }

  const transferId = options?.transferId ?? crypto.randomUUID().replaceAll("-", "_");
  const chunks: Array<BulkTransferChunk> = [];
  for (let offset = 0; offset < bytes.byteLength; offset += chunkBytes) {
    const chunkBytesValue = bytes.slice(offset, Math.min(offset + chunkBytes, bytes.byteLength));
    chunks.push({
      kind: "chunk",
      transferId,
      offset,
      bytes: chunkBytesValue.byteLength,
      data: encodeBase64Url(chunkBytesValue),
      sha256: await sha256Hex(chunkBytesValue),
    });
  }

  return {
    totalBytes: bytes.byteLength,
    sha256: await sha256Hex(bytes),
    chunks,
  };
}

export async function verifyBulkTransferChunk(chunk: BulkTransferChunk): Promise<Uint8Array> {
  const bytes = decodeBase64Url(chunk.data);
  if (bytes.byteLength !== chunk.bytes) {
    throw new Error(
      `Bulk transfer chunk byte count mismatch at offset ${chunk.offset}: expected ${chunk.bytes}, received ${bytes.byteLength}.`,
    );
  }
  if ((await sha256Hex(bytes)) !== chunk.sha256) {
    throw new Error(`Bulk transfer chunk integrity mismatch at offset ${chunk.offset}.`);
  }
  return bytes;
}

export async function assembleBulkTransfer(transfer: ChunkedBulkTransfer): Promise<Uint8Array> {
  const chunksByOffset = new Map<number, BulkTransferChunk>();
  const accepted: Array<{ readonly chunk: BulkTransferChunk; readonly bytes: Uint8Array }> = [];
  let nextOffset = 0;

  for (const chunk of transfer.chunks) {
    const previous = chunksByOffset.get(chunk.offset);
    if (previous !== undefined) {
      if (
        previous.bytes !== chunk.bytes ||
        previous.sha256 !== chunk.sha256 ||
        previous.data !== chunk.data
      ) {
        throw new Error(
          `Bulk transfer has a conflicting duplicate chunk at offset ${chunk.offset}.`,
        );
      }
      continue;
    }
    if (chunk.offset !== nextOffset) {
      throw new Error(
        `Bulk transfer chunk offset mismatch: expected ${nextOffset}, received ${chunk.offset}.`,
      );
    }
    const bytes = await verifyBulkTransferChunk(chunk);
    chunksByOffset.set(chunk.offset, chunk);
    accepted.push({ chunk, bytes });
    nextOffset += bytes.byteLength;
  }

  if (nextOffset !== transfer.totalBytes) {
    throw new Error(
      `Bulk transfer total byte count mismatch: expected ${transfer.totalBytes}, received ${nextOffset}.`,
    );
  }

  const output = new Uint8Array(transfer.totalBytes);
  for (const entry of accepted) output.set(entry.bytes, entry.chunk.offset);
  if ((await sha256Hex(output)) !== transfer.sha256) {
    throw new Error("Bulk transfer payload integrity mismatch.");
  }
  return output;
}
